import React from 'react';
import { CompositePointDefinitionDto } from '@api/FitnessActivityClient';
import { DatePicker } from '@components/date-picker';
import { Select } from '@components/select';
import { Spinner } from '@components/spinner';
import { TextField } from '@components/textfield';
import {
    ChallengeLeaderboardType,
    ChallengePageEntrySupportDto,
    LeaderboardDateFilterType,
    LeaderboardGenderMode,
    LeaderboardMetric,
    WorkoutCategory,
    WorkoutTypeV2
} from '@core/api/ChallengeClient';
import {
    CHALLENGE_LEADERBOARD_TYPE_OPTIONS,
    DATE_FILTER_TYPE_OPTIONS,
    DISTANCE_WORKOUT_CATEGORY_OPTIONS,
    GENDER_MODE_OPTIONS,
    METRIC_OPTIONS,
    TIME_WORKOUT_CATEGORY_OPTIONS
} from './constants';
import { formatDateForApi, parseDateString } from './helpers';

interface DateFilterTypeSelectorProps {
    value: LeaderboardDateFilterType;
    onChange: (value: LeaderboardDateFilterType) => void;
}

export const DateFilterTypeSelector: React.FC<DateFilterTypeSelectorProps> = ({
    value,
    onChange
}) => (
    <div className="w-[150px]">
        <Select
            label="Filter Type"
            value={value}
            options={DATE_FILTER_TYPE_OPTIONS}
            onChange={onChange}
        />
    </div>
);

interface CustomDateRangeProps {
    dateFrom?: string;
    dateTo?: string;
    onDateFromChange: (value: string | undefined) => void;
    onDateToChange: (value: string | undefined) => void;
}

export const CustomDateRange: React.FC<CustomDateRangeProps> = ({
    dateFrom,
    dateTo,
    onDateFromChange,
    onDateToChange
}) => (
    <>
        <DatePicker
            label="Date From"
            className="w-[170px]"
            required
            value={parseDateString(dateFrom)}
            onChange={(value) => onDateFromChange(formatDateForApi(value))}
        />
        <DatePicker
            label="Date To"
            className="w-[170px]"
            required
            value={parseDateString(dateTo)}
            minDate={parseDateString(dateFrom) || undefined}
            onChange={(value) => onDateToChange(formatDateForApi(value))}
        />
    </>
);

interface MetricSelectorProps {
    value: LeaderboardMetric;
    onChange: (value: LeaderboardMetric) => void;
}

export const MetricSelector: React.FC<MetricSelectorProps> = ({
    value,
    onChange
}) => (
    <div className="w-[150px]">
        <Select
            label="Metric"
            value={value}
            options={METRIC_OPTIONS}
            onChange={onChange}
        />
    </div>
);

interface WorkoutCategorySelectorProps {
    value?: WorkoutCategory;
    onChange: (value: WorkoutCategory | undefined) => void;
    metric?: LeaderboardMetric;
}

export const WorkoutCategorySelector: React.FC<
    WorkoutCategorySelectorProps
> = ({ value, onChange, metric }) => {
    const getFilteredOptions = () => {
        if (metric === LeaderboardMetric.DISTANCE) {
            return DISTANCE_WORKOUT_CATEGORY_OPTIONS;
        } else if (metric === LeaderboardMetric.TIME) {
            return TIME_WORKOUT_CATEGORY_OPTIONS;
        } else {
            throw new Error(`Invalid metric ${metric}`);
        }
    };

    return (
        <div className="w-[150px]">
            <Select
                label="Workout Category"
                value={value}
                options={[
                    { label: 'None', value: undefined },
                    ...getFilteredOptions()
                ]}
                onChange={onChange}
            />
        </div>
    );
};

interface WorkoutTypeSelectorProps {
    value?: WorkoutTypeV2;
    onChange: (value: WorkoutTypeV2 | undefined) => void;
    options: WorkoutTypeV2[];
}

export const WorkoutTypeSelector: React.FC<WorkoutTypeSelectorProps> = ({
    value,
    onChange,
    options
}) => (
    <div className="w-[170px]">
        <Select
            label="Workout Type"
            value={value}
            options={[
                { label: 'All workout types', value: undefined },
                ...options.map((option) => ({ label: option, value: option }))
            ]}
            onChange={onChange}
            disabled={options.length === 0}
        />
    </div>
);

interface RewardCurrencyInputProps {
    value?: string;
    onChange: (value: string | undefined) => void;
}

export const RewardCurrencyInput: React.FC<RewardCurrencyInputProps> = ({
    value,
    onChange
}) => (
    <TextField
        type="text"
        className="w-[150px]"
        label="Reward Currency Code"
        value={value || ''}
        onChange={(next) => onChange(String(next) || undefined)}
        placeholder="e.g., MOVES"
    />
);

interface ChallengeSelectorProps {
    value?: number;
    onChange: (value: number | undefined) => void;
    challenges: ChallengePageEntrySupportDto[];
    loading?: boolean;
}

export const ChallengeSelector: React.FC<ChallengeSelectorProps> = ({
    value,
    onChange,
    challenges,
    loading
}) => (
    <div className="flex w-[220px] items-end gap-2">
        <Select
            label="Challenge"
            required
            value={value}
            options={[
                { label: 'Select challenge...', value: undefined },
                ...challenges.map((challenge) => ({
                    label:
                        challenge.presentation.name.en ||
                        `Challenge ${challenge.id}`,
                    value: challenge.id
                }))
            ]}
            onChange={onChange}
            disabled={loading}
        />
        {loading && <Spinner size="sm" className="mb-2.5" />}
    </div>
);

interface ChallengeLeaderboardTypeSelectorProps {
    value?: ChallengeLeaderboardType;
    onChange: (value: ChallengeLeaderboardType | undefined) => void;
}

export const ChallengeLeaderboardTypeSelector: React.FC<
    ChallengeLeaderboardTypeSelectorProps
> = ({ value, onChange }) => (
    <div className="w-[160px]">
        <Select
            label="Leaderboard"
            required
            value={value}
            options={[
                { label: 'Select...', value: undefined },
                ...CHALLENGE_LEADERBOARD_TYPE_OPTIONS
            ]}
            onChange={onChange}
        />
    </div>
);

interface GenderModeSelectorProps {
    value: LeaderboardGenderMode;
    onChange: (value: LeaderboardGenderMode) => void;
}

export const GenderModeSelector: React.FC<GenderModeSelectorProps> = ({
    value,
    onChange
}) => (
    <div className="w-[180px]">
        <Select
            label="Gender split"
            value={value}
            options={GENDER_MODE_OPTIONS}
            onChange={onChange}
        />
    </div>
);

interface CompositePointKeySelectorProps {
    value?: string;
    onChange: (value: string | undefined) => void;
    definitions: CompositePointDefinitionDto[];
    loading?: boolean;
}

export const CompositePointKeySelector: React.FC<
    CompositePointKeySelectorProps
> = ({ value, onChange, definitions, loading }) => (
    <div className="flex w-[200px] items-end gap-2">
        <Select
            label="Point Type Key"
            value={value}
            options={[
                { label: 'Select definition...', value: undefined },
                ...definitions.map((d) => ({
                    label: `${d.pointTypeKey}${!d.enabled ? ' (disabled)' : ''}`,
                    value: d.pointTypeKey
                }))
            ]}
            onChange={onChange}
            disabled={loading}
        />
        {loading && <Spinner size="sm" className="mb-2.5" />}
    </div>
);
